import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FlaskConical, FileCheck2, Snowflake, ShieldCheck, Lock, Boxes } from "lucide-react";
import Seo from "@/components/Seo";

const PILLARS = [
  {
    Icon: FlaskConical,
    title: "Reference-Grade Sourcing",
    body: "Every compound in our catalog is sourced and prepared to a reference-grade specification. We select material by chemical identity, structure, and specification — supplied as sealed, lyophilized vials with the milligram content stated on each product.",
  },
  {
    Icon: FileCheck2,
    title: "Transparent Labeling",
    body: "Every vial is labeled with the compound, form, and milligram content stated on its product page. Compound specifications — form, strength, CAS, sequence and molecular details — are published on each product page.",
  },
  {
    Icon: Snowflake,
    title: "Temperature-Conscious Handling",
    body: "Materials are handled with temperature and light sensitivity in mind. Vials are stored lyophilized and shipped in protective, discreet packaging to help preserve integrity in transit to your laboratory.",
  },
  {
    Icon: Boxes,
    title: "Inventory Integrity",
    body: "We track stock at the vial level and label every shipment against its order. What arrives is exactly the catalog item and quantity you selected — no substitutions, no approximations.",
  },
  {
    Icon: ShieldCheck,
    title: "Research-Use-Only Stance",
    body: "All products are sold strictly for in-vitro laboratory research use only. They are not drugs, foods, cosmetics, or supplements, and are not for human or animal use. We do not provide, and cannot provide, dosing, administration, or usage guidance.",
  },
  {
    Icon: Lock,
    title: "Compliance & Access",
    body: "Access to the research catalog requires a research-eligibility attestation, which we record. Checkout is completed securely on-site by card, and orders capture your agreement to our Terms and Research-Use-Only policy.",
  },
];

export default function Standards() {
  return (
    <main className="pt-32 pb-28" data-testid="standards-page">
      <Seo title="Our Standards" path="/standards"
        description="How Molecularix Biolabs sources, documents, and handles reference-grade research materials — with a clear Research Use Only stance." />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Our Standards</p>
          <h1 className="font-serif text-5xl md:text-6xl font-light leading-none mb-6">
            Built for the<br />modern laboratory.
          </h1>
          <p className="text-[#A1A1AA] leading-relaxed max-w-2xl text-base md:text-lg">
            Molecularix Biolabs is a supplier of reference-grade peptides and research chemicals for
            in-vitro laboratory research. We are not a pharmacy, clinical laboratory, medical testing
            facility, or pharmaceutical manufacturer — our focus is simple: sourcing, documentation,
            careful handling, and disciplined compliance.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-px bg-zinc-900 mt-16 border border-zinc-900">
          {PILLARS.map(({ Icon, title, body }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
              className="bg-[#0A0A0A] p-8 md:p-10"
              data-testid={`standard-pillar-${i}`}
            >
              <Icon size={22} className="text-[#D4AF37] mb-5" />
              <h2 className="font-serif text-2xl font-light mb-3">{title}</h2>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 border border-zinc-900 bg-[#0C0C0C] p-8 md:p-10" data-testid="standards-disclaimer">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Important</p>
          <p className="text-[#71717A] text-sm leading-relaxed">
            Products offered by Molecularix Biolabs LLC are intended strictly for in-vitro research use
            only. Nothing on this site is a statement of efficacy, benefit, or suitability for human or
            animal use, and no product is intended to diagnose, treat, cure, or prevent any condition.
          </p>
        </div>

        <div className="mt-14 pt-8 border-t border-zinc-900 flex flex-wrap gap-4">
          <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] hover:underline" data-testid="standards-catalog-link">Browse the catalog →</Link>
          <Link to="/contact" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Contact us</Link>
        </div>
      </div>
    </main>
  );
}
